import './index.scss'

import { useLayoutEffect, useRef, useState } from 'react'

import LoadingIndicator from '@components/LoadingIndicator'
import { Launchpad, launchpadURL } from '@interfaces/launchpad.interface'

import iphoneFrame from 'url:@assets/images/iphone17_frame_portrait.png';
import linkArrowIcon from 'url:@assets/icons/linkArrow.svg';

// The iPhone 17 the frame is drawn from, in points: its screen is 1206x2622
// pixels at 3x, which is exactly the frame image's transparent screen. The
// page is laid out at this size and then scaled down to fit, so it wraps and
// arranges itself just as it does on the phone rather than at the few hundred
// pixels the preview has to spare.
const DEVICE = {
  width: 402,
  height: 874,
  // Kept clear by the Dynamic Island's status bar, and the home indicator.
  safeAreaTop: 62,
  safeAreaBottom: 34
};

// The Launchpad as people see it: its live page, at its real address, framed
// in a phone. Showing the real thing means the preview can't drift from what
// Launchpad-Web actually renders.
export default function LaunchpadViewPreview(props: {
  launchpad: Launchpad;
}) {
  const url = launchpadURL(props.launchpad);

  // The page is reloaded whenever the Launchpad changes, so an edit made here
  // shows up without a manual refresh.
  const version = JSON.stringify(props.launchpad.metadata.attributes);

  // How far the device-sized page is shrunk to fit the frame's screen. The
  // frame scales with the layout, so this follows its width as it changes.
  const screenRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number | null>(null);

  useLayoutEffect(() => {
    const screen = screenRef.current;

    if (!screen) return;

    const measure = () => setScale(screen.clientWidth / DEVICE.width);

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(screen);

    return () => observer.disconnect();
  }, []);

  return (
    <div className='launchpad-view-preview'>
      <div className='phone-shell'>
        <div className='phone-screen' ref={screenRef}>
          <LivePage
            key={`${url}|${version}`}
            url={url}
            title={`${props.launchpad.metadata.attributes.name} preview`}
            scale={scale} />
        </div>

        <img src={iphoneFrame} alt='' aria-hidden='true' className='iphone-frame' />
      </div>

      <a className='launchpad-url' href={url} target='_blank' rel='noopener noreferrer'>
        <span>{url.replace(/^https?:\/\//, '')}</span>
        <img src={linkArrowIcon} alt='' aria-hidden='true' />
      </a>
    </div>
  )
}

// Keyed by its caller, so each load starts with the spinner showing. The
// spinner sits outside the scaled device, so it isn't shrunk with the page.
function LivePage(props: { url: string; title: string; scale: number | null }) {
  const [hasLoaded, setHasLoaded] = useState(false);

  return <>
    {hasLoaded ? null : <div className='preview-loading'>
      <LoadingIndicator fullScreen={false} />
    </div>}

    {/* Not drawn until the frame has been measured, so it never shows at full
        size for a frame before shrinking. */}
    {props.scale !== null ? <div
      className='device-viewport'
      style={{
        width: DEVICE.width,
        height: DEVICE.height,
        paddingTop: DEVICE.safeAreaTop,
        paddingBottom: DEVICE.safeAreaBottom,
        transform: `scale(${props.scale})`
      }}>
      <iframe
        src={props.url}
        title={props.title}
        className={hasLoaded ? 'preview-frame is-loaded' : 'preview-frame'}
        onLoad={() => setHasLoaded(true)} />
    </div> : null}
  </>
}
